import './privacy-consent.css';
import { initializeAnalytics } from './analytics';
import { activateModal } from './ui/modal';

const consent = window.kilncatPrivacyConsent;
if (!consent) throw new Error('Privacy consent bootstrap must run before application modules');
initializeAnalytics(consent);

const element = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attributes: Record<string, string | boolean | (() => void)> = {},
  children: Array<Node | string> = [],
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  for (const [name, value] of Object.entries(attributes)) {
    if (name === 'onclick' && typeof value === 'function') node.addEventListener('click', value);
    else if (typeof value === 'boolean') (node as unknown as Record<string, boolean>)[name] = value;
    else node.setAttribute(name, String(value));
  }
  node.append(...children);
  return node;
};

const policyLink = (): HTMLAnchorElement =>
  element('a', { href: '/privacy', target: '_blank', rel: 'noreferrer' }, ['Privacy Policy']);

let banner: HTMLElement | null = null;
let overlay: HTMLElement | null = null;
let deactivateModal = (): void => {};

const settingsButton = element(
  'button',
  {
    class: 'privacy-settings-button',
    type: 'button',
    onclick: () => openPreferences(),
  },
  ['Privacy choices'],
);
settingsButton.hidden = true;

type PreferenceViewOptions = {
  modal: boolean;
  initial?: boolean;
};

function closePreferences(): void {
  deactivateModal();
  deactivateModal = (): void => {};
  overlay?.remove();
  overlay = null;
  if (banner && !consent.snapshot().hasDecision) banner.hidden = false;
}

function preferenceControls(options: PreferenceViewOptions): HTMLElement {
  const snapshot = consent.snapshot();
  let analyticsEnabled = snapshot.choices.analytics;
  let replayEnabled = snapshot.choices.replay;

  const analytics = element('input', {
    type: 'checkbox',
    checked: analyticsEnabled,
    disabled: snapshot.signalDenied,
    'aria-describedby': 'consent-analytics-description',
  }) as HTMLInputElement;
  const replay = element('input', {
    type: 'checkbox',
    checked: replayEnabled,
    disabled: snapshot.signalDenied || !analyticsEnabled,
    'aria-describedby': 'consent-replay-description',
  }) as HTMLInputElement;

  analytics.addEventListener('change', () => {
    analyticsEnabled = analytics.checked;
    if (!analyticsEnabled) {
      replayEnabled = false;
      replay.checked = false;
    }
    replay.disabled = snapshot.signalDenied || !analyticsEnabled;
  });
  replay.addEventListener('change', () => (replayEnabled = replay.checked));

  const save = (): void => {
    consent.save({ analytics: analyticsEnabled, replay: replayEnabled }, 'customize');
    if (options.modal) closePreferences();
  };
  const reject = (): void => {
    consent.rejectAll(options.modal ? 'settings' : 'banner');
    if (options.modal) closePreferences();
  };
  const accept = (): void => {
    consent.acceptAll(options.modal ? 'settings' : 'banner');
    if (options.modal) closePreferences();
  };

  const panel = element(
    'section',
    {
      class: `consent-panel consent-preferences${options.modal ? ' consent-modal' : ''}`,
      ...(options.modal
        ? {
            role: 'dialog',
            'aria-modal': 'true',
          }
        : { role: 'region' }),
      'aria-labelledby': 'consent-preferences-title',
      tabindex: options.modal ? '-1' : '0',
    },
    [
      element('div', { class: 'consent-heading' }, [
        element('div', {}, [
          element('span', { class: 'consent-kicker' }, ['Privacy controls']),
          element('h2', { id: 'consent-preferences-title' }, ['Choose what KilnCat may measure']),
        ]),
        ...(options.modal
          ? [
              element(
                'button',
                {
                  class: 'consent-close',
                  type: 'button',
                  'aria-label': 'Close privacy choices',
                  onclick: closePreferences,
                },
                ['×'],
              ),
            ]
          : []),
      ]),
      element('p', { class: 'consent-intro' }, [
        'KilnCat counts visits and a few key actions without analytics cookies. You can allow cookies to help us understand how the product is used across visits, and choose separately whether to allow masked session recordings. Read our ',
        policyLink(),
        '.',
      ]),
      ...(snapshot.signalDenied
        ? [
            element('p', { class: 'consent-signal', role: 'status' }, [
              'Your browser has asked us not to track you, so analytics and session recordings are off.',
            ]),
          ]
        : []),
      element('div', { class: 'consent-purpose-list' }, [
        purposeRow({
          title: 'Essential storage',
          description:
            'Authentication, security, editor recovery, and this consent record. Always active.',
          control: element('input', {
            type: 'checkbox',
            checked: true,
            disabled: true,
            'aria-label': 'Essential storage always active',
          }),
        }),
        purposeRow({
          title: 'Product analytics across visits',
          description:
            'Allow analytics cookies so we can understand how people use KilnCat over time. If you sign in, accepted analytics can be linked to your account. If you leave this off, we receive only anonymous counts for visits and a few key actions, plus feedback you choose to send.',
          descriptionId: 'consent-analytics-description',
          control: analytics,
        }),
        purposeRow({
          title: 'Session recordings',
          description:
            'Allow us to review masked recordings of how KilnCat is used so we can troubleshoot and improve it. Typed input is hidden, and texture canvases are not recorded. Requires product analytics across visits.',
          descriptionId: 'consent-replay-description',
          control: replay,
        }),
      ]),
      element('p', { class: 'consent-support-note' }, [
        'Reject all keeps anonymous counts only and turns off analytics cookies and recordings. A browser privacy signal turns all analytics off. Support remains available either way.',
      ]),
      element('div', { class: 'consent-actions consent-preference-actions' }, [
        element('button', { class: 'consent-choice', type: 'button', onclick: reject }, [
          'Reject all',
        ]),
        ...(snapshot.signalDenied
          ? []
          : [
              element('button', { class: 'consent-save', type: 'button', onclick: save }, [
                'Save choices',
              ]),
              element('button', { class: 'consent-choice', type: 'button', onclick: accept }, [
                'Accept all',
              ]),
            ]),
      ]),
    ],
  );
  return panel;
}

function purposeRow({
  title,
  description,
  descriptionId,
  control,
}: {
  title: string;
  description: string;
  descriptionId?: string;
  control: HTMLElement;
}): HTMLElement {
  return element('label', { class: 'consent-purpose' }, [
    element('span', { class: 'consent-purpose-copy' }, [
      element('strong', {}, [title]),
      element('span', { ...(descriptionId ? { id: descriptionId } : {}) }, [description]),
    ]),
    element('span', { class: 'consent-switch' }, [
      control,
      element('span', { 'aria-hidden': 'true' }),
    ]),
  ]);
}

function initialBanner(): HTMLElement {
  const snapshot = consent.snapshot();
  if (snapshot.signalDenied) {
    return element(
      'section',
      {
        class: 'consent-panel consent-banner',
        role: 'region',
        'aria-labelledby': 'consent-title',
      },
      [
        element('div', { class: 'consent-banner-copy' }, [
          element('span', { class: 'consent-kicker' }, ['Privacy signal honored']),
          element('h2', { id: 'consent-title' }, ['Analytics are off']),
          element('p', {}, [
            'Your browser has asked us not to track you. Analytics and session recordings are off. Read our ',
            policyLink(),
            '.',
          ]),
        ]),
        element('div', { class: 'consent-actions' }, [
          element(
            'button',
            {
              class: 'consent-choice',
              type: 'button',
              onclick: () => consent.rejectAll('privacy-signal'),
            },
            ['Got it'],
          ),
        ]),
      ],
    );
  }

  const customize = (): void => openPreferences(true);
  return element(
    'section',
    {
      class: 'consent-panel consent-banner',
      role: 'region',
      'aria-labelledby': 'consent-title',
    },
    [
      element('div', { class: 'consent-banner-copy' }, [
        element('span', { class: 'consent-kicker' }, ['Your privacy choices']),
        element('h2', { id: 'consent-title' }, [
          'Choose whether KilnCat may learn from your visit',
        ]),
        element('p', {}, [
          'KilnCat counts visits and a few key actions without analytics cookies. Reject all keeps these anonymous counts only. Accept all also allows analytics across visits and masked session recordings. Read our ',
          policyLink(),
          '.',
        ]),
      ]),
      element('div', { class: 'consent-actions' }, [
        element(
          'button',
          { class: 'consent-choice', type: 'button', onclick: () => consent.rejectAll('banner') },
          ['Reject all'],
        ),
        element('button', { class: 'consent-customize', type: 'button', onclick: customize }, [
          'Customize',
        ]),
        element(
          'button',
          { class: 'consent-choice', type: 'button', onclick: () => consent.acceptAll('banner') },
          ['Accept all'],
        ),
      ]),
    ],
  );
}

function openPreferences(fromBanner = false): void {
  if (overlay) return;
  if (fromBanner && banner) banner.hidden = true;
  overlay = element('div', { class: 'consent-overlay' }, [
    preferenceControls({ modal: true, initial: fromBanner }),
  ]);
  const panel = overlay.firstElementChild as HTMLElement;
  overlay.addEventListener('pointerdown', (event) => {
    if (event.target === overlay) closePreferences();
  });
  document.body.appendChild(overlay);
  deactivateModal = activateModal(overlay, panel, { onEscape: closePreferences });
}

function render(snapshot = consent.snapshot()): void {
  banner?.remove();
  banner = null;
  if (!snapshot.hasDecision) {
    banner = initialBanner();
    document.body.appendChild(banner);
    settingsButton.hidden = true;
  } else {
    settingsButton.hidden = false;
  }
}

function mount(): void {
  if (document.querySelector('.privacy-settings-button')) return;
  // Public pages expose withdrawal with the other durable policy controls in
  // their footer. Product surfaces without a public footer can dispatch the
  // settings event from an appropriate local control (the editor uses its
  // status bar) rather than adding a gesture-blocking fixed button.
  document.querySelector('.site-footer .footer-links')?.appendChild(settingsButton);
  window.addEventListener('kilncat:open-privacy-settings', () => openPreferences());
  consent.subscribe(render);
  render();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
else mount();
